import { MicVAD } from "@ricky0123/vad-web";
import { postVoiceEvent } from "../../api.js";
const DUCK_VOLUME = 0.5;
const FULL_VOLUME = 1;
const VOLUME_FADE_MS = 80;
const MIN_SPEECH_MS = 600;
const REDEMPTION_MS = 600;
const VAD_WEB_VERSION = "0.0.30";
const ONNX_RUNTIME_WEB_VERSION = "1.22.0";
const CDN_BASE_ASSET_PATH = `https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@${VAD_WEB_VERSION}/dist/`;
const CDN_ONNX_WASM_BASE_PATH = `https://cdn.jsdelivr.net/npm/onnxruntime-web@${ONNX_RUNTIME_WEB_VERSION}/dist/`;
class VoiceBargeInController {
  constructor() {
    this.vad = null;
    this.deps = null;
    this.isDucked = false;
    this.duckStartedAt = null;
    this.fadeRafId = null;
    this.started = false;
  }
  async start(ctx) {
    if (this.started) return true;
    this.deps = ctx;
    const stream = ctx.getStream();
    if (!stream) {
      this.emitVadEvent("vad.init_failed", { reason: "no_stream" });
      ctx.onInitFailed();
      return false;
    }
    try {
      this.vad = await MicVAD.new({
        baseAssetPath: CDN_BASE_ASSET_PATH,
        onnxWASMBasePath: CDN_ONNX_WASM_BASE_PATH,
        startOnLoad: false,
        positiveSpeechThreshold: 0.55,
        negativeSpeechThreshold: 0.35,
        minSpeechMs: MIN_SPEECH_MS,
        redemptionMs: REDEMPTION_MS,
        getStream: async () => {
          var _a;
          const s = (_a = this.deps) == null ? void 0 : _a.getStream();
          if (!s) {
            throw new Error("Voice barge-in mic stream unavailable");
          }
          return s;
        },
        // Shared mic stream — never stop tracks on VAD pause/destroy.
        pauseStream: async () => {
        },
        resumeStream: async () => {
          var _a;
          const s = (_a = this.deps) == null ? void 0 : _a.getStream();
          if (!s) {
            throw new Error("Voice barge-in mic stream unavailable");
          }
          return s;
        },
        onSpeechStart: () => {
          void this.handleSpeechStart();
        },
        onSpeechRealStart: () => {
          void this.handleSpeechRealStart();
        },
        onVADMisfire: () => {
          void this.handleVadMisfire();
        },
        onSpeechEnd: () => {
        },
        onFrameProcessed: () => {
        }
      });
      await this.vad.start();
      this.started = true;
      return true;
    } catch (err) {
      this.emitVadEvent("vad.init_failed", {
        reason: err instanceof Error ? err.message : String(err)
      });
      ctx.onInitFailed();
      this.vad = null;
      return false;
    }
  }
  async stop() {
    this.cancelVolumeFade();
    this.restoreVolumeImmediate();
    this.isDucked = false;
    this.duckStartedAt = null;
    if (this.vad) {
      try {
        await this.vad.pause();
        await this.vad.destroy();
      } catch {
      }
      this.vad = null;
    }
    this.deps = null;
    this.started = false;
  }
  shouldGateBargeIn() {
    const deps = this.deps;
    if (!deps) return false;
    return deps.isAssistantAudible() || deps.isResponseInFlight();
  }
  async handleSpeechStart() {
    var _a, _b;
    if (!this.shouldGateBargeIn()) return;
    this.duckStartedAt = Date.now();
    this.fadeVolumeTo(DUCK_VOLUME);
    this.isDucked = true;
    this.emitVadEvent("vad.ducked", {
      duckVolume: DUCK_VOLUME,
      assistantAudible: ((_a = this.deps) == null ? void 0 : _a.isAssistantAudible()) ?? false,
      responseInFlight: ((_b = this.deps) == null ? void 0 : _b.isResponseInFlight()) ?? false
    });
  }
  async handleSpeechRealStart() {
    if (!this.shouldGateBargeIn()) {
      if (this.isDucked) {
        this.restoreVolumeImmediate();
        this.isDucked = false;
        this.duckStartedAt = null;
      }
      return;
    }
    const duckDurationMs = this.duckStartedAt != null ? Math.max(0, Date.now() - this.duckStartedAt) : void 0;
    this.restoreVolumeImmediate();
    this.isDucked = false;
    this.duckStartedAt = null;
    const deps = this.deps;
    if (!deps) return;
    const responseInFlight = deps.isResponseInFlight();
    if (responseInFlight) {
      deps.sendDataChannel({ type: "response.cancel" });
    }
    this.emitVadEvent("vad.confirmed_interrupt", {
      duckDurationMs,
      assistantAudible: deps.isAssistantAudible(),
      responseInFlight,
      cancelSent: responseInFlight
    });
  }
  async handleVadMisfire() {
    if (!this.isDucked) return;
    const duckDurationMs = this.duckStartedAt != null ? Math.max(0, Date.now() - this.duckStartedAt) : void 0;
    this.restoreVolumeImmediate();
    this.isDucked = false;
    this.duckStartedAt = null;
    this.emitVadEvent("vad.misfire", {
      duckDurationMs,
      minSpeechMs: MIN_SPEECH_MS
    });
  }
  fadeVolumeTo(target) {
    var _a;
    const audioEl = (_a = this.deps) == null ? void 0 : _a.getAudioEl();
    if (!audioEl) return;
    this.cancelVolumeFade();
    const startVolume = audioEl.volume;
    const startTime = performance.now();
    const step = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(1, elapsed / VOLUME_FADE_MS);
      audioEl.volume = startVolume + (target - startVolume) * t;
      if (t < 1) {
        this.fadeRafId = requestAnimationFrame(step);
      } else {
        this.fadeRafId = null;
        audioEl.volume = target;
      }
    };
    this.fadeRafId = requestAnimationFrame(step);
  }
  restoreVolumeImmediate() {
    var _a;
    this.cancelVolumeFade();
    const audioEl = (_a = this.deps) == null ? void 0 : _a.getAudioEl();
    if (audioEl) {
      audioEl.volume = FULL_VOLUME;
    }
  }
  cancelVolumeFade() {
    if (this.fadeRafId != null) {
      cancelAnimationFrame(this.fadeRafId);
      this.fadeRafId = null;
    }
  }
  emitVadEvent(type, payload) {
    var _a;
    const deps = this.deps;
    if (!deps) return;
    (_a = deps.log) == null ? void 0 : _a.call(deps, type, payload);
    void postVoiceEvent({
      conversationId: deps.conversationId,
      events: [{ type, t: Date.now(), ...payload }],
      headersToInject: deps.getAuthHeaders()
    });
  }
}
function buildServerBargeInFallbackSessionUpdate() {
  return {
    type: "session.update",
    session: {
      type: "realtime",
      audio: {
        input: {
          turn_detection: {
            type: "semantic_vad",
            eagerness: "auto",
            interrupt_response: true,
            create_response: true
          }
        }
      }
    }
  };
}
export {
  VoiceBargeInController,
  buildServerBargeInFallbackSessionUpdate
};
//# sourceMappingURL=useVoiceBargeIn.js.map
